export function createForgeSpeechPlayer(
    browser: () => Pick<Window & typeof globalThis, 'AudioContext'> = () => window,
    request: typeof fetch = (...args) => fetch(...args),
) {
    let context: AudioContext | undefined;
    let source: AudioBufferSourceNode | undefined;
    let pending: AbortController | undefined;
    let generation = 0;
    const stop = () => {
        generation++;
        pending?.abort();
        pending = undefined;
        if (source) { source.onended = null; source.stop(); source.disconnect(); source = undefined; }
    };
    const unlock = async () => {
        if (!context || context.state === 'closed') context = new (browser().AudioContext)();
        if (context.state !== 'running') await context.resume();
        if (context.state !== 'running') throw new Error('Audio is suspended. Select Read aloud directly to enable playback.');
    };
    return {
        async configure(signal: AbortSignal) {
            const response = await request('/api/agent/speak', {
                signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]), cache: 'no-store',
            });
            const body: unknown = await response.json();
            if (!response.ok || !body || typeof body !== 'object' || !('enabled' in body) || typeof body.enabled !== 'boolean') {
                const reason = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? `${body.error} ` : '';
                throw new Error(`Speech configuration unavailable. ${reason}Reload to retry.`);
            }
            if (body.enabled && typeof browser().AudioContext !== 'function') throw new Error('Audio playback is unavailable in this browser.');
            return body.enabled;
        },
        unlock,
        stop,
        dispose() {
            stop();
            const closing = context;
            context = undefined;
            if (closing && closing.state !== 'closed') void closing.close().catch(() => console.error('[agent-speech] Could not close audio context.'));
        },
        async play(text: string, ended: () => void, failed: (message: string) => void) {
            stop();
            const current = generation;
            const active = new AbortController();
            pending = active;
            try {
                const response = await request('/api/agent/speak', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
                    signal: AbortSignal.any([active.signal, AbortSignal.timeout(60000)]), cache: 'no-store',
                });
                if (!response.ok) {
                    const body: unknown = await response.json();
                    throw new Error(body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
                        ? body.error : 'Forge speech failed. Select Read aloud to retry.');
                }
                if (response.headers.get('content-type') !== 'audio/wav') throw new Error('Invalid speech audio response.');
                const bytes = await response.arrayBuffer();
                if (bytes.byteLength > 2 * 1024 * 1024 || bytes.byteLength < 44) throw new Error('Invalid speech audio size.');
                if (current !== generation) return;
                if (!context || context.state !== 'running') throw new Error('Select Read aloud or enable Auto-speak with a direct tap to enable audio.');
                const audio = await context.decodeAudioData(bytes);
                if (current !== generation || !context) return;
                if (context.state !== 'running') throw new Error('Audio is suspended. Select Read aloud directly to retry.');
                source = context.createBufferSource();
                source.buffer = audio;
                source.connect(context.destination);
                source.onended = () => {
                    if (current !== generation) return;
                    source?.disconnect(); source = undefined; ended();
                };
                source.start();
            } catch (error) {
                if (current === generation) failed(error instanceof Error ? error.message : 'Forge speech playback failed.');
            } finally { if (pending === active) pending = undefined; }
        },
    };
}
